import { inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from "@ngrx/operators";
import { Store , createSelector } from "@ngrx/store";

import { catchError, exhaustMap, filter, map, merge, of, switchMap, tap } from "rxjs";

import { BulkEditCalendarEventGQL, JobsV3PageGQL, UpdateJobGQL } from "../../../generated/graphql.generated";
import { generateMutationContext } from "../../core/auth/session";
import { selectJobId } from "../../job/job-tool.reducer";
import { WorkOrdersActions } from "../../job/job-workorders/job-workorders.actions";

import { CancelAndCloseDialogActions } from "./cancel-and-close-dialog.actions";
import { cancelAndCloseDialogFeature } from "./cancel-and-close-dialog.reducer";
import { selectCancelAndCloseJob, selectCancelAndCloseJobFranchiseId, selectCancelAndClosePendingTransactions } from "./cancel-and-close-dialog.selectors";

export const openDialog = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
  return actions$.pipe(
    ofType(WorkOrdersActions.cancelMenuItemClicked),
    concatLatestFrom(() => store.select(selectJobId)),
    map(([ { eventId }, jobId ]) => CancelAndCloseDialogActions.openCancelAndCloseDialogTriggered({
      jobId,
      trigger: 'cancel',
      eventIds: [eventId],
    })),
  );
}, { functional: true, dispatch: true });

export const fetchJob = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  jobsV3GQL = inject(JobsV3PageGQL),
) => {
  return actions$.pipe(
    ofType(CancelAndCloseDialogActions.openCancelAndCloseDialogTriggered),
    concatLatestFrom(() => store.select(selectCancelAndCloseJob)),
    filter(([ _action, job ]) => !job),
    switchMap(([ { jobId } ]) => jobsV3GQL.fetch({
      jobId,
      resolve: ['discounts', 'users', 'locations', 'distances', 'artifacts', 'charges'],
    }, {
      fetchPolicy: 'network-only',
    }).pipe(
      map((res) => {
        const [ job ] = res.data.jobs.jobs;

        if (!job) {
          return CancelAndCloseDialogActions.fetchJobError({ error: new Error('Job not found') });
        }

        return CancelAndCloseDialogActions.fetchJobSuccess({ job });
      }),
      catchError((error) => of(CancelAndCloseDialogActions.fetchJobError({ error }))),
    )),
  );
}, { functional: true, dispatch: true });

export const cancelEvents = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  bulkEditCalendarEventGQL = inject(BulkEditCalendarEventGQL),
) => {

  const continueWithCancel$ = actions$.pipe(
    ofType(CancelAndCloseDialogActions.continueButtonClicked),
    filter(({ command }) => command === 'cancel'),
  );

  const cancelEventsOnly$ = actions$.pipe(
    ofType(CancelAndCloseDialogActions.cancelEventsOnlyButtonClicked),
  );

  return merge(continueWithCancel$, cancelEventsOnly$).pipe(
    concatLatestFrom(() => [
      store.select(createSelector(
        cancelAndCloseDialogFeature.selectEventsToCancel,
        (eventsToCancel) => Object.entries(eventsToCancel)
          .filter(([, checked]) => checked)
          .map(([id]) => id),
      )),
      store.select(cancelAndCloseDialogFeature.selectJobId),
    ]),
    filter(([ _action, eventIds ]) => eventIds.length > 0),
    exhaustMap(([ _action, eventIds, jobId ]) => bulkEditCalendarEventGQL.mutate({
      edits: eventIds.map((id) => ({ id, edit: { status: 'cancelled' } })),
    }).pipe(
      tap((res) => {
        if (!res.data?.bulkEditCalendarEvent?.events) {
          throw new Error('Failed to cancel events');
        }
      }),
      map((res) => CancelAndCloseDialogActions.cancelEventsSuccess({
        updatedEvents: res.data.bulkEditCalendarEvent.events,
        jobId,
      })),
      catchError((error) => of(CancelAndCloseDialogActions.cancelEventsError({ error }))),
    )),
  );
}, { functional: true, dispatch: true });

export const cancelEventsAndCloseJob = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  updateJobGQL = inject(UpdateJobGQL),
) => {
  return actions$.pipe(
    ofType(CancelAndCloseDialogActions.closeJobButtonClicked),
    concatLatestFrom(() => store.select(createSelector(
      cancelAndCloseDialogFeature.selectClosedReason,
      cancelAndCloseDialogFeature.selectJobId,
      cancelAndCloseDialogFeature.selectEventsToCancel,
      selectCancelAndCloseJobFranchiseId,
      selectCancelAndClosePendingTransactions,
      (closedReason, jobId, eventsToCancel, franchiseId, { payLinkCount }) => ({
        close: Boolean(closedReason) && Object.values(eventsToCancel).every(Boolean),
        vars: {
          updateJobs: {
            jobId,
            closeJob: true,
            closedReason,
            cancelPendingPayLinks: payLinkCount > 0,
            editCalendarEvents: {
              edits: Object.entries(eventsToCancel)
                .filter(([, checked]) => checked)
                .map(([ id ]) => ({ id, edit: { status: 'cancelled' } })),
            },
          },
        },
        opts: {
          context: generateMutationContext({ zone: franchiseId }),
        },
      }),
    ))),
    filter(([ _action, { close } ]) => close),
    exhaustMap(([ _action, { vars, opts } ]) => updateJobGQL.mutate(vars, opts).pipe(
      tap((res) => {
        if (!res.data?.updateJobs?.isSuccess || !res.data?.updateJobs?.jobs?.[0]) {
          throw new Error('Failed to cancel events and close job');
        }
      }),
      map((res) => CancelAndCloseDialogActions.cancelAndCloseSuccess({
        updatedJob: res.data!.updateJobs!.jobs[0],
      })),
      catchError((error) => of(CancelAndCloseDialogActions.cancelAndCloseError({ error }))),
    )),
  );
}, { functional: true, dispatch: true });
